import { useIsMutating, useQueryClient } from '@tanstack/react-query'
import { getQueryKey } from '@trpc/react-query'
import { useGlobalSearchParams } from 'expo-router'

import type { UserPayload } from '@rescuebase/api/client-types'

import Snackbar from '@/components/snackbar/Snackbar'
import { useAppStore } from '@/store/useAppStore'
import trpc from '@/utils/trpc'

const UserService = {
  useCurrentUser() {
    const currentOrganization =
      useAppStore.use.currentOrganization().organization

    const { data: user, isLoading } = trpc.user.current.useQuery(
      {
        organizationId: currentOrganization?.id || undefined,
      },
      {
        enabled: !!currentOrganization?.id,
      }
    )

    return { user, isLoading, currentOrganization }
  },
  useUser() {
    const params = useGlobalSearchParams<{ id: string }>()
    const userId = params.id || ''

    const currentOrganization =
      useAppStore.use.currentOrganization().organization

    const { data: user, isLoading } = trpc.user.byId.useQuery(
      {
        id: userId,
        organizationId: currentOrganization?.id || '',
      },
      {
        enabled: !!currentOrganization?.id,
      }
    )

    return { user, isLoading, currentOrganization }
  },

  useUserList() {
    const organizationId =
      useAppStore.use.currentOrganization().organization?.id
    const includeDeactivated = useAppStore.use.includeDeactivated()
    const {
      data: users,
      error,
      isLoading,
    } = trpc.user.list.useQuery(
      {
        includeDeactivated,
        organizationId: organizationId || '',
      },
      {
        enabled: !!organizationId,
      }
    )
    return { users, error, isLoading }
  },

  useDeactivateMutation() {
    const queryClient = useQueryClient()

    return trpc.user.deactivate.useMutation({
      onSuccess: (_data, { id, organizationId }) => {
        queryClient.invalidateQueries({ queryKey: getQueryKey(trpc.user.list) })
        queryClient.setQueryData(
          getQueryKey(
            trpc.user.byId,
            {
              id: id,
              organizationId: organizationId,
            },
            'query'
          ),
          (user: UserPayload | undefined) =>
            user
              ? {
                  ...user,
                  deactivatedAt: new Date(),
                  roles: [],
                }
              : undefined
        )
      },
      onError: () =>
        Snackbar.error('There was a problem deactivating this user'),
    })
  },
  useReactivateMutation() {
    const queryClient = useQueryClient()

    return trpc.user.reactivate.useMutation({
      onSuccess: (_data, { id, organizationId }) => {
        queryClient.invalidateQueries({ queryKey: getQueryKey(trpc.user.list) })
        queryClient.setQueryData(
          getQueryKey(
            trpc.user.byId,
            {
              id: id,
              organizationId: organizationId,
            },
            'query'
          ),
          (user: UserPayload | undefined) =>
            user
              ? {
                  ...user,
                  deactivatedAt: null,
                }
              : undefined
        )
      },
      onError: () =>
        Snackbar.error('There was a problem reactivating this user'),
    })
  },
  useUpdateMutation() {
    const queryClient = useQueryClient()
    const currentOrganization =
      useAppStore.use.currentOrganization().organization

    return trpc.user.update.useMutation({
      onError: () => {
        Snackbar.error('There was a problem saving this user')
      },
      onSuccess: (updatedData) => {
        queryClient.setQueryData(
          getQueryKey(
            trpc.user.current,
            {
              organizationId: currentOrganization?.id || undefined,
            },
            'query'
          ),
          () => updatedData
        )
        queryClient.invalidateQueries({ queryKey: getQueryKey(trpc.user.list) })
      },
    })
  },

  useOrgUpdateMutation() {
    const queryClient = useQueryClient()
    const currentOrganization =
      useAppStore.use.currentOrganization().organization

    return trpc.user.updateOrgDetails.useMutation({
      onError: () => {
        Snackbar.error('There was a problem saving this user')
      },
      onSuccess: (updatedData) => {
        queryClient.setQueryData(
          getQueryKey(
            trpc.user.byId,
            {
              id: updatedData.id,
              organizationId: currentOrganization?.id || '',
            },
            'query'
          ),
          () => updatedData
        )
        queryClient.invalidateQueries({ queryKey: getQueryKey(trpc.user.list) })
      },
    })
  },
  useIsSaving() {
    const mutationKey = getQueryKey(trpc.user.updateOrgDetails)
    return useIsMutating({ mutationKey }) > 0
  },
  useTriggerRemindersMutation() {
    return trpc.user.triggerReminders.useMutation({
      onError: () => {
        Snackbar.error('There was a problem triggering reminders')
      },
      onSuccess: () => {
        Snackbar.info('Triggered reminders for this account')
      },
    })
  },
}

export default UserService
